import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AdminTreeNode {
  id: number;
  node_id: string;
  name: string;
  type: 'operation' | 'action';
  expandable: boolean;
  level: number;
  parent_id: string | null;
  icon?: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  children?: AdminTreeNode[];
}

export interface AdminTreeResponse {
  success: boolean;
  tree: AdminTreeNode[];
  total_nodes: number;
  message: string;
}

export interface AdminTreeFlatResponse {
  success: boolean;
  nodes: AdminTreeNode[];
  total_count: number;
  message: string;
}

export interface AdminNodeCreatePayload {
  node_id: string;
  name: string;
  type: 'operation' | 'action';
  expandable: boolean;
  level: number;
  parent_id: string | null;
  icon?: string;
  description?: string;
  sort_order?: number;
}

export interface AdminNodeUpdatePayload {
  name?: string;
  type?: 'operation' | 'action';
  icon?: string;
  description?: string;
  sort_order?: number;
  is_active?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private apiBaseUrl = `${environment.apiBaseUrl}/api/admin`;

  constructor(private http: HttpClient) {}

  /**
   * Get the complete admin tree structure (hierarchical)
   */
  getAdminTree(): Observable<AdminTreeResponse> {
    return this.http.get<AdminTreeResponse>(`${this.apiBaseUrl}/tree`);
  }

  /**
   * Get all admin tree nodes in flat structure
   */
  getAdminTreeFlat(): Observable<AdminTreeFlatResponse> {
    return this.http.get<AdminTreeFlatResponse>(`${this.apiBaseUrl}/tree/flat`);
  }

  /**
   * Get admin tree nodes by level
   */
  getAdminTreeByLevel(level: number): Observable<any> {
    return this.http.get(`${this.apiBaseUrl}/tree/level/${level}`);
  }

  /**
   * Get child nodes of a specific parent
   */
  getAdminTreeByParent(parentId: string): Observable<any> {
    return this.http.get(`${this.apiBaseUrl}/tree/parent/${parentId}`);
  }

  /**
   * Create a new admin tree node
   */
  createAdminNode(payload: AdminNodeCreatePayload): Observable<any> {
    console.log('Creating admin node with payload:', payload);
    return this.http.post(`${this.apiBaseUrl}/tree/nodes`, payload);
  }

  /**
   * Update an admin tree node
   */
  updateAdminNode(nodeId: string, payload: AdminNodeUpdatePayload): Observable<any> {
    return this.http.put(`${this.apiBaseUrl}/tree/nodes/${nodeId}`, payload);
  }

  /**
   * Delete an admin tree node
   */
  deleteAdminNode(nodeId: string): Observable<any> {
    return this.http.delete(`${this.apiBaseUrl}/tree/nodes/${nodeId}`);
  }

  /**
   * Refresh the admin tree (utility method for forcing refresh)
   */
  refreshAdminTree(): Observable<AdminTreeResponse> {
    return this.getAdminTree();
  }
} 